/**
 * Shared renderer for a full ResearchReport.
 * Used by both the inline post-submit success state and the detail route.
 */
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { AlertTriangle } from "lucide-react";
import { useT } from "../../lib/i18n";

export default function ResearchReportView({ report }) {
  const t = useT();
  if (!report) return null;
  return (
    <div data-testid="research-report" className="space-y-6">
      {report.mocked && (
        <Card>
          <CardContent className="pt-6 flex items-start gap-3">
            <AlertTriangle className="size-5 text-amber-500 mt-0.5" />
            <div className="text-sm text-muted-foreground">{t("research.mocked_warning")}</div>
          </CardContent>
        </Card>
      )}
      {report.status === "failed" && (
        <Card>
          <CardContent className="pt-6 flex items-start gap-3">
            <AlertTriangle className="size-5 text-destructive mt-0.5" />
            <div className="text-sm">
              <div className="font-medium">{t("research.parse_error")}</div>
              <div className="text-muted-foreground mt-1 break-all">
                {report.parse_error || t("research.error")}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t("research.summary")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {report.summary ? (
            <p className="text-base leading-relaxed">{report.summary}</p>
          ) : (
            <p className="text-sm text-muted-foreground">—</p>
          )}
          <div className="flex items-center gap-2">
            <Badge variant="outline" data-testid="report-confidence">
              {t("research.confidence")}: {(Number(report.confidence) * 100 || 0).toFixed(0)}%
            </Badge>
            {report.region && <Badge variant="secondary">{report.region}</Badge>}
            {report.industry && <Badge variant="secondary">{report.industry}</Badge>}
            {report.locale && <Badge variant="outline" className="font-mono uppercase">{report.locale}</Badge>}
          </div>
        </CardContent>
      </Card>

      <Section title={t("research.market")} testid="section-market">
        {report.market && Object.keys(report.market).length > 0 ? (
          <dl className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            {Object.entries(report.market).map(([k, v]) => (
              <div key={k}>
                <dt className="text-xs text-muted-foreground uppercase tracking-wider">{k}</dt>
                <dd className="mt-1">{typeof v === "object" ? JSON.stringify(v) : String(v)}</dd>
              </div>
            ))}
          </dl>
        ) : <Empty />}
      </Section>

      <Section title={t("research.audience")} testid="section-audience">
        {(report.audience || []).length > 0 ? (
          <div className="space-y-4">
            {report.audience.map((a, i) => (
              <div key={i} className="border rounded-md p-4 space-y-2 text-sm">
                <div className="font-medium">
                  {a.segment || `Segment ${i + 1}`}
                  {a.age_range ? ` · ${a.age_range}` : ""}
                </div>
                {a.pains?.length > 0 && <Bulleted label="Pains" items={a.pains} />}
                {a.desires?.length > 0 && <Bulleted label="Desires" items={a.desires} />}
                {a.channels?.length > 0 && <Bulleted label="Channels" items={a.channels} />}
              </div>
            ))}
          </div>
        ) : <Empty />}
      </Section>

      <Section title={t("research.competitors")} testid="section-competitors">
        {(report.competitors || []).length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {report.competitors.map((c, i) => (
              <div key={i} className="border rounded-md p-4 text-sm space-y-1">
                <div className="font-medium">{c.name}</div>
                <div className="text-muted-foreground text-xs">{c.positioning}</div>
                {c.strengths?.length > 0 && <Bulleted label="+ Strengths" items={c.strengths} />}
                {c.weaknesses?.length > 0 && <Bulleted label="− Weaknesses" items={c.weaknesses} />}
              </div>
            ))}
          </div>
        ) : <Empty />}
      </Section>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <SimpleListCard title={t("research.trends")} items={report.trends} testid="section-trends" />
        <SimpleListCard title={t("research.ad_angles")} items={report.ad_angles} testid="section-ad-angles" />
        <SimpleListCard title={t("research.objections")} items={report.objections} testid="section-objections" />
        <SimpleListCard title={t("research.triggers")} items={report.triggers} testid="section-triggers" />
        <SimpleListCard title={t("research.risks")} items={report.compliance_risks} testid="section-risks" />
        <SimpleListCard title={t("research.differentiation")} items={report.differentiation} testid="section-differentiation" />
      </div>
    </div>
  );
}

function Section({ title, children, testid }) {
  return (
    <Card data-testid={testid}>
      <CardHeader><CardTitle className="text-base">{title}</CardTitle></CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

function SimpleListCard({ title, items, testid }) {
  return (
    <Card data-testid={testid}>
      <CardHeader><CardTitle className="text-base">{title}</CardTitle></CardHeader>
      <CardContent>
        {Array.isArray(items) && items.length > 0 ? (
          <ul className="space-y-1.5 text-sm list-disc list-outside pl-4">
            {items.map((x, i) => (
              <li key={i}>{typeof x === "object" ? JSON.stringify(x) : String(x)}</li>
            ))}
          </ul>
        ) : <Empty />}
      </CardContent>
    </Card>
  );
}

function Bulleted({ label, items }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <ul className="list-disc pl-4 mt-1 space-y-0.5">
        {items.map((x, i) => <li key={i}>{String(x)}</li>)}
      </ul>
    </div>
  );
}

function Empty() {
  return <div className="text-xs text-muted-foreground">—</div>;
}

export function StatusBadge({ status }) {
  const t = useT();
  if (!status || status === "succeeded") {
    return (
      <Badge data-testid={`status-${status || "succeeded"}`} variant="default">
        {t("research.status_succeeded")}
      </Badge>
    );
  }
  if (status === "failed") {
    return (
      <Badge data-testid="status-failed" variant="destructive">
        {t("research.status_failed")}
      </Badge>
    );
  }
  if (status === "fallback") {
    return (
      <Badge data-testid="status-fallback" variant="secondary">
        {t("research.status_fallback")}
      </Badge>
    );
  }
  return <Badge variant="outline">{status}</Badge>;
}
